import { environment } from 'src/environments/environment';

/**
 * Reading the *bytes* of an S3 object from the browser.
 *
 * Profile photos, signatures and uploaded documents reach the frontend as
 * presigned S3 URLs (`Document::getDocUrlAttribute`, which calls
 * `temporaryUrl`). Those display fine in an `<img>` — a plain image load needs
 * no CORS — but every path that wants the bytes back out fails:
 *
 *   - `fetch()` / `HttpClient` with `responseType: 'blob'` are refused outright;
 *   - drawing the image into a canvas taints it, so `toDataURL()` throws a
 *     `SecurityError`, which is how this first showed up: an ID download that
 *     produced nothing at all.
 *
 * The cause is not in this repo. The bucket `AWS_BUCKET` points at
 * (`dswd-documents-private-production` in prod) carries **no CORS
 * configuration** — a GET with an `Origin` header comes back 200 with not one
 * `Access-Control-*` header, and an `OPTIONS` preflight comes back 403. Note
 * that it is not one of the buckets `serverless.yml` creates, so the real fix
 * is a `put-bucket-cors` on that bucket by whoever holds AWS access, not a
 * change to the stack.
 *
 * Until that happens everything goes through the API instead, which is
 * same-origin-ish and does send the header: `GET /api/v1/public/image-proxy`
 * (`PublicController::proxy`). That endpoint is restricted to our own storage
 * buckets and throttled, because it is unauthenticated.
 *
 * TEMPORARY. When the bucket grows a CORS rule, delete this file and put the
 * plain `fetch(url)` back in its callers.
 */

/** Route on the API that re-serves an S3 object with permissive CORS headers. */
const PROXY_PATH = 'api/v1/public/image-proxy';

/**
 * Whether `url` has to be proxied to be readable.
 *
 * Anything already inline (`data:`, `blob:`) or served from our own origin is
 * left alone — proxying those would be a pointless round trip through the API,
 * and `blob:` URLs are not resolvable from the server at all.
 */
export function needsImageProxy(url: string): boolean {
  if (!url || url.startsWith('data:') || url.startsWith('blob:')) return false;

  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    // Not parseable as a URL — a relative asset path, so same-origin.
    return false;
  }
}

/**
 * The URL to actually request for `url`: the proxy when it is cross-origin,
 * `url` itself when it is not.
 */
export function proxiedImageUrl(url: string): string {
  if (!needsImageProxy(url)) return url;

  const base = environment.apiUrl.endsWith('/')
    ? environment.apiUrl
    : `${environment.apiUrl}/`;

  return `${base}${PROXY_PATH}?url=${encodeURIComponent(url)}`;
}

/**
 * `fetch`, but for an image that may live on S3.
 *
 * @throws if the request fails or the proxy answers with an error status. A
 *         non-OK response is raised rather than passed on, because the body in
 *         that case is the proxy's JSON error and a caller reading it as an
 *         image would fail somewhere much less obvious.
 */
export async function fetchImage(url: string): Promise<Blob> {
  const response = await fetch(proxiedImageUrl(url));

  if (!response.ok) {
    throw new Error(`Could not fetch image (${response.status}): ${url}`);
  }

  return response.blob();
}

/** The image at `url` as a data URL, fetched through the proxy when needed. */
export async function imageToDataUrl(url: string): Promise<string> {
  const blob = await fetchImage(url);

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
